<template>
    <div class="folder-navigator-component">
        <fade-and-slide>
            <div v-if="collectionsOwner != null" :class="$style['main-item']">
                <template v-if="collectionsOwner !== selectedCollection">
                    <a
                        @click.prevent="
                            $emit('navigate-to-collection', collectionsOwner)
                        "
                        :href="
                            getCollectionUrl(
                                collectionsOwner,
                                viewMode,
                                baseUrl,
                                value,
                                root ?? [],
                                matchedLogicalName,
                                matchedLogicalNameEntityGuid,
                                matchedLogicalNameModuleGuid,
                                matchedLogicalNameSubModuleGuid
                            ) ?? '#'
                        "
                        :class="$style['link']"
                        >{{ collectionsOwner.Name }}</a
                    >
                </template>
                <template v-else>
                    <a
                        @click.prevent="
                            $emit(
                                'navigate-to-collection',
                                collectionsOwner.parent
                            )
                        "
                        :href="
                            collectionsOwner.parent != null
                                ? getCollectionUrl(
                                      collectionsOwner.parent,
                                      viewMode,
                                      baseUrl,
                                      value,
                                      root ?? [],
                                      matchedLogicalName,
                                      matchedLogicalNameEntityGuid,
                                      matchedLogicalNameModuleGuid,
                                      matchedLogicalNameSubModuleGuid
                                  ) ?? '#'
                                : '#'
                        "
                    >
                        <span :class="$style['back-arrow']"></span>
                    </a>
                    {{ collectionsOwner.Name }}
                </template>
            </div>
        </fade-and-slide>
        <div
            :class="[
                $style['items'],
                collectionsOwner != null ? $style['indented'] : '',
            ]"
        >
            <template v-for="collection in collections">
                <template v-if="getVisible(collection)">
                    <div
                        :class="[
                            $style['item'],
                            collection === selectedCollection
                                ? $style['active']
                                : '',
                        ]"
                    >
                        <a
                            @click.prevent="
                                $emit('navigate-to-collection', collection)
                            "
                            :href="
                                getCollectionUrl(
                                    collection,
                                    viewMode,
                                    baseUrl,
                                    value,
                                    root ?? [],
                                    matchedLogicalName,
                                    matchedLogicalNameEntityGuid,
                                    matchedLogicalNameModuleGuid,
                                    matchedLogicalNameSubModuleGuid
                                ) ?? '#'
                            "
                            :class="$style['link']"
                            ><span :class="$style['forward-arrow']"></span>
                            {{ collection.Name }}</a
                        >
                        <div
                            v-if="collection.Settings?.previewSubitems != null"
                        >
                            <div
                                v-for="{
                                    col,
                                    depth,
                                    colScopedRoot,
                                } in findFirstPreviewableItems(collection)"
                                :class="[
                                    $style['sub-item'],
                                    'sub-item-depth-' + depth,
                                ]"
                            >
                                <checkbox-item
                                    :id="'collectionCheckBox_' + col.Id"
                                    :checked="false"
                                    :name="col.Name"
                                    :href="
                                        getCollectionUrl(
                                            col,
                                            viewMode,
                                            baseUrl,
                                            value,
                                            root ?? [],
                                            matchedLogicalName,
                                            matchedLogicalNameEntityGuid,
                                            matchedLogicalNameModuleGuid,
                                            matchedLogicalNameSubModuleGuid
                                        ) ?? undefined
                                    "
                                    @select="
                                        $emit('navigate-to-collection', {
                                            collection: col,
                                            scopedRoot: colScopedRoot,
                                        })
                                    "
                                ></checkbox-item>
                            </div>
                        </div>
                    </div>
                </template>
            </template>
        </div>
    </div>
</template>

<style module lang="scss">
:global(.sub-item-depth-0) {
    margin-left: 0;
}
:global(.sub-item-depth-1) {
    margin-left: 10px;
}
:global(.sub-item-depth-2) {
    margin-left: 20px;
}

.back-arrow {
    background-image: url("../../../../../../../mthemes/odendo.dk/images/back-arrow.svg");
    display: inline-block;
    width: 0.9em !important;
    height: 0.9em !important;
    background-size: contain;
    background-repeat: no-repeat;
    margin-left: 2px;
    margin-right: 2px;
    vertical-align: baseline;
    background-position: center;
}

.forward-arrow {
    @extend .back-arrow;
    background-image: url("../../../../../../../mthemes/odendo.dk/images/forward-arrow.svg");
}

.link {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow-x: hidden;
    max-width: 100%;
    display: block;
}

.main-item,
.item {
    font-size: 14px;
    color: #272727;
    margin-bottom: 8px;
}

.sub-item {
    font-weight: 500;
}

.items {
    transition: margin 0.4s ease-in-out;
    margin-left: 0px;
}

.items.indented {
    margin-left: 5px;
}

.main-item {
    font-weight: 700;
}

.items:first-child .item:first-child {
    margin-top: 0;
}

.item {
    font-weight: 600;
    margin-top: 15px;
    margin-bottom: 15px;
}

.item:not(.disabled) {
    cursor: pointer;
}

.item.disabled {
    cursor: default;
}

.item.active {
    font-weight: 700;
    cursor: default;
}

.transition-enter,
.transition-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

.transition-move {
    transition: transform 0.3s;
}

@keyframes bounce-in {
    0% {
        opacity: 0;
        filter: grayscale(1);
    }

    100% {
        opacity: 1;
        filter: grayscale(0);
    }
}
</style>

<script setup lang="ts">
import { type PropType, computed } from "vue";
import FadeAndSlide from "../../../../shared/FadeAndSlide.vue";
import { type MappedProductCollectionModel } from "../../models";
import {
    findCollectionById,
    flattenAllCollectionsWithParents,
} from "../../collection-methods";
import { getCollectionUrl } from "../../collection-url";
import CheckboxItem from "../common/checkbox-item.vue";

defineEmits(["navigate-to-collection"]);

type PreviewItemEntry = {
    col: MappedProductCollectionModel;
    depth: number;
    colScopedRoot: MappedProductCollectionModel | undefined;
};

function findFirstPreviewableItems(collection: MappedProductCollectionModel) {
    const items: PreviewItemEntry[] = [];

    const limit = collection.Settings?.previewSubitems;

    if (limit == null) return items;

    const subCols = flattenAllCollectionsWithParents([collection]);

    for (const subCol of subCols) {
        if (items.length === limit) {
            // console.debug("break by limit");
            break;
        }
        if (subCol.parents.length == 0) {
            // console.debug("continue: no parents: " + subCol.collection.Name);
            continue;
        }
        const revParents = subCol.parents.concat().reverse();
        const qualifyingParentSettingIndex = revParents.findIndex(
            (x) => x.FilterViewMode == 1
        );
        if (qualifyingParentSettingIndex == -1) {
            // console.debug(
            //     "continue: no filterviewmode 1 parent found: " +
            //         subCol.collection.Name
            // );
            continue;
        }
        // console.log(subCol.parents);
        if (qualifyingParentSettingIndex < 1) {
            // console.debug(
            //     "continue: not deep enough: " + subCol.collection.Name
            // );
            continue;
        }
        if (qualifyingParentSettingIndex > 3) {
            // console.debug("continue: too deep: " + subCol.collection.Name);
            continue;
        }
        // console.debug("adding!: " + subCol.collection.Name);
        items.push({
            col: subCol.collection,
            depth: subCol.parents.length - 1,
            colScopedRoot: revParents.find((x) => x.FilterViewMode == 1),
        });
    }
    return items;
}

const props = defineProps({
    siteGuid: Number,
    productListGuid: Number,
    value: Array as PropType<number[][]>,
    root: Array as PropType<MappedProductCollectionModel[]>,
    scopedRoot: Object as PropType<MappedProductCollectionModel>,
    baseUrl: String,
    getVisible: {
        type: Function,
        required: true,
    },
    viewMode: {
        type: Number,
        required: true,
    },
    // findCollectionById: {
    //     type: Function as PropType<
    //         (
    //             collectionId: number,
    //             mappedRoot: ProductCollectionModel[] | MappedProductCollectionModel[] | undefined
    //         ) => ProductCollectionModel
    //     >,
    //     required: true,
    // },
    //getFlattenedCollections: Function,
    matchedLogicalName: String,
    matchedLogicalNameEntityGuid: Number,
    matchedLogicalNameModuleGuid: Number,
    matchedLogicalNameSubModuleGuid: Number,
});

const mappedRoot = computed(() => {
    if (props.root == null) return props.root;
    if (props.scopedRoot != null) {
        return props.scopedRoot.SubCollections;
    }
    return props.root;
});
const collectionsOwner = computed(() => {
    if (props.root == null) return props.root;
    const selectedCol = selectedCollection.value;

    let collection = null;
    if (selectedCol != null) {
        collection = selectedCol;
        if (
            !Array.isArray(collection.SubCollections) ||
            collection.SubCollections.length <= 0
        )
            collection = collection.parent;
    }

    if (collection === props.scopedRoot) collection = null;

    return collection;
});
const collections = computed(() => {
    if (props.root == null) return props.root;
    if (collectionsOwner.value == null) return mappedRoot.value;
    return collectionsOwner.value.SubCollections;
});
const selectedCollection = computed(() => {
    if (props.value == null) return props.value;
    if (props.value.length === 0) return undefined;
    if (props.value[0].length == 0) return undefined;
    const collectionId = props.value[0][0];
    return findCollectionById(collectionId, mappedRoot.value);
});

// onMounted(() => {
//     console.debug(mappedRoot.value);
// });
</script>
